import React from "react";

type RenderAction =
    | { type: "link"; text: string; url: string }
    | { type: "postback"; text: string; payload: string };

type PreviewBlock =
    | { type: "text"; text: string }
    | { type: "qr"; url: string }
    | { type: "actions"; actions: RenderAction[] };

type MessengerPreviewPanelProps = {
    resolvedTitle: string;
    previewBlocks: PreviewBlock[];
    AVAILABLE_PARAMS: string[];
    sampleParams: Record<string, any>;
    onPostback?: (payload: string) => void;
    // optional: show/hide sample params section
    showParams?: boolean;
};

export function MessengerPreviewPanel({
    resolvedTitle,
    previewBlocks,
    AVAILABLE_PARAMS,
    sampleParams,
    onPostback,
    showParams = true,
}: MessengerPreviewPanelProps) {
    return (
        <div style={{ border: "1px solid #ddd", borderRadius: 12, padding: 14 }}>
            <h3 style={{ margin: 0 }}>Preview</h3>

            {/* Messenger-like container */}
            <div
                style={{
                    marginTop: 10,
                    border: "1px solid #eee",
                    borderRadius: 14,
                    overflow: "hidden",
                    background: "#fff",
                }}
            >
                {/* Header (Messenger web style) */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "12px 14px",
                        borderBottom: "1px solid #eee",
                        background: "#fff",
                    }}
                >
                    <div
                        style={{
                            width: 34,
                            height: 34,
                            borderRadius: "50%",
                            background: "#e7f0ff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 800,
                            color: "#1877F2", // FB-ish blue
                            fontFamily:
                                'system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif',
                        }}
                        title="Page"
                    >
                        P
                    </div>

                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: 13, color: "#111", lineHeight: 1.2 }}>
                            Your Page
                        </div>
                        <div style={{ fontSize: 12, color: "#6b7280" }}>
                            Previewing template message
                        </div>
                    </div>

                    <div style={{ marginLeft: "auto", fontSize: 12, color: "#9ca3af" }}>
                        Web
                    </div>
                </div>

                {/* Conversation area */}
                <div
                    style={{
                        padding: 14,
                        height: 420,
                        overflow: "auto",
                        background:
                            "linear-gradient(180deg, #f6f7f9 0%, #f2f3f5 100%)",
                    }}
                >
                    {/* Title as a small “system” note */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            marginBottom: 10,
                        }}
                    >
                        <div
                            style={{
                                fontSize: 12,
                                color: "#6b7280",
                                background: "#ffffffcc",
                                border: "1px solid #eee",
                                padding: "6px 10px",
                                borderRadius: 999,
                            }}
                        >
                            {resolvedTitle}
                        </div>
                    </div>

                    {/* Messages */}
                    <div style={{ display: "grid", gap: 10 }}>
                        {previewBlocks.map((b, idx) => {
                            if (b.type === "text") {
                                // Split by lines but keep in one bubble
                                const text = b.text.replace(/\n{3,}/g, "\n\n");
                                return (
                                    <div key={idx} style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                                        {/* Left avatar (bubble sender) */}
                                        <div
                                            style={{
                                                width: 26,
                                                height: 26,
                                                borderRadius: "50%",
                                                background: "#e5e7eb",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: 12,
                                                color: "#111",
                                                flex: "0 0 auto",
                                            }}
                                            title="Page"
                                        >
                                            P
                                        </div>

                                        {/* Messenger bubble (left) */}
                                        <div
                                            style={{
                                                maxWidth: "78%",
                                                background: "#e4e6eb",
                                                color: "#111",
                                                borderRadius: 18,
                                                padding: "10px 12px",
                                                fontSize: 14,
                                                lineHeight: 1.35,
                                                whiteSpace: "pre-wrap",
                                                boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
                                            }}
                                        >
                                            {text.trim()}
                                        </div>
                                    </div>
                                );
                            }

                            if (b.type === "qr") {
                                return (
                                    <div key={idx} style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                                        <div
                                            style={{
                                                width: 26,
                                                height: 26,
                                                borderRadius: "50%",
                                                background: "#e5e7eb",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: 12,
                                                color: "#111",
                                                flex: "0 0 auto",
                                            }}
                                        >
                                            P
                                        </div>

                                        <div
                                            style={{
                                                background: "#e4e6eb",
                                                borderRadius: 18,
                                                padding: 10,
                                                boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
                                            }}
                                        >
                                            <img
                                                src={b.url}
                                                alt="QR"
                                                style={{
                                                    width: 160,
                                                    height: 160,
                                                    borderRadius: 14,
                                                    border: "1px solid #eee",
                                                    display: "block",
                                                    background: "#fff",
                                                }}
                                                onError={(e) => {
                                                    (e.currentTarget as HTMLImageElement).style.display = "none";
                                                }}
                                            />
                                            <div style={{ fontSize: 11, color: "#6b7280", marginTop: 8, maxWidth: 160 }}>
                                                QR code
                                            </div>
                                        </div>
                                    </div>
                                );
                            }

                            if (b.type === "actions") {
                                // Messenger quick replies look like pill buttons under message
                                return (
                                    <div key={idx} style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                                        <div style={{ width: 26 }} />

                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, maxWidth: "84%" }}>
                                            {b.actions.map((a, i) => {
                                                if (a.type === "link") {
                                                    return (
                                                        <a
                                                            key={i}
                                                            href={a.url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            style={{
                                                                padding: "8px 12px",
                                                                borderRadius: 999,
                                                                border: "1px solid #dbeafe",
                                                                background: "#fff",
                                                                color: "#1877F2",
                                                                textDecoration: "none",
                                                                fontWeight: 700,
                                                                fontSize: 13,
                                                                boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
                                                            }}
                                                        >
                                                            {a.text || "Open"}
                                                        </a>
                                                    );
                                                }

                                                return (
                                                    <button
                                                        key={i}
                                                        type="button"
                                                        onClick={() => onPostback?.(a.payload)}
                                                        style={{
                                                            padding: "8px 12px",
                                                            borderRadius: 999,
                                                            border: "1px solid #dbeafe",
                                                            background: "#fff",
                                                            color: "#1877F2",
                                                            cursor: "pointer",
                                                            fontWeight: 800,
                                                            fontSize: 13,
                                                            boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
                                                        }}
                                                        title={`payload=${a.payload}`}
                                                    >
                                                        {a.text || "Send"}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            }

                            return null;
                        })}
                    </div>
                </div>

                {/* Composer bar (fake) */}
                <div
                    style={{
                        borderTop: "1px solid #eee",
                        padding: 12,
                        background: "#fff",
                        display: "flex",
                        gap: 10,
                        alignItems: "center",
                    }}
                >
                    <div
                        style={{
                            flex: 1,
                            background: "#f0f2f5",
                            borderRadius: 999,
                            padding: "10px 12px",
                            color: "#9ca3af",
                            fontSize: 13,
                        }}
                    >
                        Aa
                    </div>
                    <div
                        style={{
                            width: 34,
                            height: 34,
                            borderRadius: "50%",
                            background: "#1877F2",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontWeight: 900,
                            fontSize: 14,
                        }}
                        title="Send"
                    >
                        ➤
                    </div>
                </div>
            </div>

            {/* Sample param panel */}
            {showParams && (
                <div style={{ marginTop: 12, borderTop: "1px solid #eee", paddingTop: 12 }}>
                    <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
                        Preview uses sample params (pass <code>params</code> prop to override):
                    </div>
                    <div style={{ display: "grid", gap: 6, fontSize: 12, color: "#333" }}>
                        {AVAILABLE_PARAMS.map((k) => (
                            <div key={k} style={{ display: "flex", gap: 8 }}>
                                <div style={{ width: 160, color: "#666" }}>{k}</div>
                                <div style={{ wordBreak: "break-all" }}>{String(sampleParams[k])}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}